import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import { numberToDollars } from '../utils'
import { DOLLAR_SIGN_GREEN } from './RetirementIncomeDonut'
import DisclosureToggle from './DisclosureToggle'
import HelpIcon from './HelpIcon'
import TooltipText from './TooltipText'

const Values = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => (p.$compact ? '16px' : '10px')};
`

const ValueGroup = styled.div`
  display: grid;

  /* Let wide amounts push the label aside instead of overlapping it. */
  grid-template-columns: minmax(90px, auto) auto;
  column-gap: 30px;
  row-gap: 2px;
  justify-content: start;

  /* Center the label against the amount instead of top-aligning it. */
  align-items: center;
`

const CompactValueGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`

const ValueLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${p => p.theme.lightestGray};
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;

  /* Remove extra line-height around the help icon. */
  & > * {
    line-height: 0;
  }
`

/*
 * Sits under the label, in the same column, so it reads as part of the same
 * value block. In the two-column layout that puts it alongside the annual
 * figure, which is where the design places it.
 */
const ValueNote = styled.div`
  color: ${p => p.theme.lightestGray};
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
`

const AMOUNT_TYPE = {
  small: { fontSize: '14px', lineHeight: '18px' },
  body: { fontSize: '18px', lineHeight: '22px' },
  headline: { fontSize: '25.3px', lineHeight: '30.93px' },
}

const ValueAmount = styled.div`
  color: ${p => p.theme.darkestGray};
  font-size: ${p => AMOUNT_TYPE[p.$size].fontSize};
  font-weight: 600;
  line-height: ${p => AMOUNT_TYPE[p.$size].lineHeight};
  white-space: nowrap;
`

const DollarSign = styled.span`
  color: ${p => (p.$muted ? p.theme.lightestGray : DOLLAR_SIGN_GREEN)};
  font-weight: 400;
`

const AmountSuffix = styled.span`
  margin-left: 6px;
  color: ${p => p.theme.lightestGray};
  font-size: 14px;
  font-weight: 300;
`

const ValuesToggle = styled(DisclosureToggle)`
  /* Keep the button from stretching across the column; center it in compact layouts. */
  align-self: ${p => (p.$compact ? 'center' : 'flex-start')};
  gap: 10px;
  margin-top: ${p => (p.$compact ? '0' : '15px')};
  color: ${p => p.theme.lightestGray};
  font-size: 14px;
  line-height: 18px;
  white-space: nowrap;
`

const Amount = ({ value, muted, size, suffix }) => (
  <ValueAmount $size={size}>
    <DollarSign $muted={muted}>$</DollarSign>
    {numberToDollars(value, true)}
    {suffix && <AmountSuffix>{suffix}</AmountSuffix>}
  </ValueAmount>
)

const ValueRow = ({ compact, headline, label, compactLabel, monthly, annual, help, note }) =>
  compact ? (
    <CompactValueGroup>
      <ValueLabel>
        {compactLabel}
        {help}
      </ValueLabel>
      <Amount value={monthly} size='body' suffix='/month' />
      {note && <ValueNote>{note}</ValueNote>}
    </CompactValueGroup>
  ) : (
    <ValueGroup>
      <Amount value={monthly} size={headline ? 'headline' : 'body'} />
      <ValueLabel>
        {label}
        {help}
      </ValueLabel>
      <Amount value={annual} muted size='small' suffix='annual' />
      {note && <ValueNote>{note}</ValueNote>}
    </ValueGroup>
  )

const RetirementIncomeValues = ({
  values,
  compact = false,
  emphasizeToday = false,
  expanded,
  onToggle,
  showAtRetirementHelp = false,
  todaysNote,
  className,
}) => (
  <Values className={className} $compact={compact}>
    <ValueRow
      compact={compact}
      headline
      label="Today's monthly value"
      compactLabel="Today's value"
      monthly={values.todaysMonthly}
      annual={values.todaysAnnual}
      note={todaysNote}
    />

    <ValuesToggle $compact={compact} expanded={expanded} onToggle={onToggle}>
      {expanded ? 'Hide value at retirement' : 'Show value at retirement'}
    </ValuesToggle>

    {expanded && (
      <ValueRow
        compact={compact}
        headline={!emphasizeToday}
        label='Monthly value at retirement'
        compactLabel='Value at retirement'
        monthly={values.atRetirementMonthly}
        annual={values.atRetirementAnnual}
        help={
          showAtRetirementHelp && (
            <HelpIcon size='custom18' tooltip={TooltipText.valueAtRetirement()} />
          )
        }
      />
    )}
  </Values>
)

RetirementIncomeValues.propTypes = {
  /** Pass values returned by `projectedIncomeValues`. */
  values: PropTypes.shape({
    todaysMonthly: PropTypes.number.isRequired,
    todaysAnnual: PropTypes.number.isRequired,
    atRetirementMonthly: PropTypes.number.isRequired,
    atRetirementAnnual: PropTypes.number.isRequired,
  }).isRequired,
  /** Stack labels above amounts and hide annual values. */
  compact: PropTypes.bool,
  /** Render the at-retirement amount smaller than today's amount. */
  emphasizeToday: PropTypes.bool,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  showAtRetirementHelp: PropTypes.bool,
  /** Extra line under today's value, such as the retired-spouse "seem low?" nudge. */
  todaysNote: PropTypes.node,
  className: PropTypes.string,
}

export default RetirementIncomeValues
